import React, { useState } from "react";
import { useFormikContext } from "formik";
import { useApolloClient } from "@apollo/client/react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { usePopupState, bindMenu, bindTrigger } from "material-ui-popup-state/hooks";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLoading } from "./LoadingProvider";
import useShipmentActions from "../hooks/useShipmentActions";
import useQueryParams from "src/hooks/useQueryParams";
import useMutation from "src/hooks/useMutation";
import {
  CREATE_INVENTORY_TRANSACTION_MUTATION,
  UPDATE_SHIPMENT_ITEMS_MUTATION,
  CREATE_IMAGE_MUTATION,
} from "src/constants/graphql/mutations";
import { GET_SHIPMENT_QUERY, ASSET_QUERY } from "src/constants/graphql/queries";
import useNotification from "../../../../../hooks/notification";
import DuplicateIcon from "../../../../icons/DuplicateIcon";
import BulkDuplicateIcon from "../../../../icons/BulkDuplicateIcon";
import BulkDuplicateModal from "./BulkDuplicateModal";
import ValidationErrorModal from "./ValidationErrorModal";
import { addShipmentBillingToAssetInput } from "../../../../../utils/billingHelpers";

const styles = {
  container: {
    position: "sticky",
    bottom: "10px",
    width: "100%",
    left: 0,
    zIndex: 99,
    backgroundColor: "white",
  },
  saveButton: {
    marginRight: "1px",
    height: 36,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  dropdownButton: {
    padding: 0,
    height: 36,
    minWidth: 40,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
};

function getInput(values) {
  const {
    attachments,
    availableQuantity,
    shipmentItemQuantity,
    assetId,
    barcode,
    palletsReceivedCount,
    expectedQuantity,
    rackLocations,
    itemNotes,
    totalPieces,
    customFields,
    ...input
  } = values;

  // Handle custom fields for new assets (duplicates)
  if (customFields) {
    const customFieldValues = Object.entries(customFields).filter(
      ([key]) => key.startsWith("field_") && /field_\d+/.test(key)
    );

    const customFieldsFormatted = customFieldValues.map(([key, value]) => {
      const customFieldTemplateId = key.replace("field_", "");
      const fieldValue = value?.toString() ?? "";

      return {
        custom_field_template_id: parseInt(customFieldTemplateId),
        value: fieldValue,
        order: 1,
      };
    });

    if (customFieldsFormatted.length > 0) {
      input.customFields = customFieldsFormatted;
    }
  }

  return input;
}

export default function FormActions({
  shipmentId,
  shipment,
  removing,
  toggleRemoveItem,
  closeItemDetails,
  asset,
}) {
  const params = useQueryParams();
  const navigate = useNavigate();
  const client = useApolloClient();
  const { submitForm, isSubmitting, values, validateForm, isValid, dirty } = useFormikContext();
  const { loading } = useLoading();
  const {
    createShipment,
    createAsset,
    createSku,
    addShipmentItemLoading,
    createAssetLoading,
  } = useShipmentActions(shipmentId);
  const [createTransactionMutation] = useMutation(CREATE_INVENTORY_TRANSACTION_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
  });
  const [updateShipmentItem] = useMutation(UPDATE_SHIPMENT_ITEMS_MUTATION, {
    refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
  });
  const [createImageMutation] = useMutation(CREATE_IMAGE_MUTATION);
  const { notifySuccess, notifyError } = useNotification();
  const isDuplicateLoading = addShipmentItemLoading || createAssetLoading;
  const isDeleteDisabled = removing || loading;
  const isSaveDisabled = loading || isSubmitting || isDuplicateLoading;
  const isDuplicate = params.get("duplicate");
  const popupState = usePopupState({ variant: "popover", popupId: "item-details-popup-menu" });
  const shipmentSerialNumbers = (shipment?.shipmentItems || [])
    .map(item => item?.asset?.serialNumber)
    .filter(Boolean);
  const bulkDuplicateModalOpen = params.get("bulkDuplicateModal") === "true";
  const [bulkDuplicateLoading, setBulkDuplicateLoading] = useState(false);
  const [bulkDuplicateProgress, setBulkDuplicateProgress] = useState(null);
  const [validationErrorOpen, setValidationErrorOpen] = useState(false);
  const { rackLocations } = asset;

  const showValidationError = () => {
    setValidationErrorOpen(true);
  };

  const handleCloseValidationError = () => {
    setValidationErrorOpen(false);
  };

  const handleSave = async () => {
    try {
      const errors = await validateForm();
      const formIsValid = Object.keys(errors).length === 0;

      // First, save the current asset
      await submitForm();

      // Then, if in duplicate mode, create a new SKU for the library
      if (isDuplicate && formIsValid) {
        const input = getInput(values);

        await createSku(input);

        // Clean up duplicate and newItem params after successful creation
        const newParams = new URLSearchParams(window.location.search);
        newParams.delete("duplicate");
        newParams.delete("newItem");
        navigate({ search: newParams.toString() });

        notifySuccess("SKU added to library successfully");
      }
    } catch (error) {
      console.error("Error saving shipment:", error);
      notifyError("Failed to save. Please try again.");
    }
  };

  const handleDuplicate = async () => {
    try {
      const errors = await validateForm();
      const formIsValid = Object.keys(errors).length === 0;

      if (!formIsValid) {
        showValidationError();
        popupState.close();
        return;
      }

      await submitForm();
      const input = addShipmentBillingToAssetInput(shipment, getInput(values));
      // Create the asset without opening the new item immediately
      const assetData = await createShipment(input, { openNewItem: false });

      if (assetData) {
        // This always creates a regular Asset (not Sku) for the shipment
        const newAsset = assetData.data.createAsset.asset;
        const shipmentItemId = assetData.shipmentItem.id;

        // Duplicate images if present
        if (asset.images && asset.images.length > 0) {
          try {
            await Promise.all(
              asset.images.map(image =>
                createImageMutation({
                  variables: {
                    assetId: newAsset.id,
                    url: image.url,
                  },
                })
              )
            );
          } catch (imageError) {
            console.error("Error duplicating images:", imageError);
          }
        }

        // Handle rack locations and inventory transactions
        if (rackLocations && rackLocations.length > 0) {
          try {
            const totalQuantity = rackLocations.reduce(
              (sum, location) => sum + (location.sub_qty || 0),
              0
            );

            if (totalQuantity > 0) {
              await updateShipmentItem({
                variables: {
                  id: shipmentItemId,
                  quantity: totalQuantity,
                  expectedQuantity: values.expectedQuantity,
                },
                refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
              });

              await createTransactionMutation({
                variables: {
                  assetId: newAsset.id,
                  code: newAsset.assetId,
                  quantity: totalQuantity,
                  notes: "",
                  senderName: "",
                  carrierName: "",
                  trackingNumber: "",
                  orderNumber: "",
                  poNumber: "",
                  rackLocations: rackLocations,
                  rackLocationsChanges: rackLocations,
                  oldRackLocations: [{ sub_qty: 0 }],
                  shipmentItemId,
                  actionSource: "transaction_modal",
                },
                refetchQueries: [
                  { query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } },
                  { query: ASSET_QUERY, variables: { id: newAsset.id } },
                ],

                awaitRefetchQueries: true,
              });
            }
          } catch (transactionError) {
            console.error("Error creating inventory transactions:", transactionError);
          }
        } else if (values.expectedQuantity) {
          try {
            await updateShipmentItem({
              variables: {
                id: shipmentItemId,
                quantity: values.shipmentItemQuantity || 0,
                expectedQuantity: values.expectedQuantity,
              },
              refetchQueries: [{ query: GET_SHIPMENT_QUERY, variables: { id: shipmentId } }],
            });
          } catch (updateError) {
            console.error("Error updating expected quantity:", updateError);
          }
        }

        // Show success notification
        notifySuccess("Item duplicated successfully");

        // Navigate to the new item after all operations are complete
        const pathname = window.location.pathname;
        const params = new URLSearchParams(location.search);
        params.set("assetId", newAsset.id);
        params.set("shipmentItemId", shipmentItemId);
        params.set("addShipmentItem", "true");
        params.delete("itemLookup");
        navigate({ pathname, search: params.toString() });
      }
    } catch (error) {
      console.error("Error duplicating shipment:", error);
      notifyError("Failed to duplicate item. Please try again.");
    }
    popupState.close();
  };

  const handleBulkDuplicate = async () => {
    const errors = await validateForm();
    const formIsValid = Object.keys(errors).length === 0;

    if (!formIsValid) {
      showValidationError();
      popupState.close();
      return;
    }

    popupState.close();
    const newParams = new URLSearchParams(window.location.search);
    newParams.set("bulkDuplicateModal", "true");
    navigate({ search: newParams.toString() });
  };

  const createSingleDuplicate = async ({
    processedInput,
    images,
    rackLocations: itemRackLocations,
    expectedQuantity,
    shipmentItemQuantity,
  }) => {
    const assetData = await createShipment(processedInput, {
      openNewItem: false,
      skipNotifications: true,
      skipRefetch: true,
      skipRefreshCache: true,
    });

    if (!assetData) return;

    const newAsset = assetData.data.createAsset.asset;
    const shipmentItemId = assetData.shipmentItem.id;

    // Run images, shipment item update, and inventory transaction in parallel.
    // These are independent: images → images table, updateShipmentItem → shipment_items table,
    // createTransaction → inventory_transactions + assets table. No shared rows.
    const parallelOps = [];

    if (images && images.length > 0) {
      parallelOps.push(
        Promise.all(
          images.map(image =>
            createImageMutation({
              variables: { assetId: newAsset.id, url: image.url },
            })
          )
        ).catch(imageError => {
          console.error("Error duplicating images:", imageError);
        })
      );
    }

    if (itemRackLocations && itemRackLocations.length > 0) {
      const totalQuantity = itemRackLocations.reduce(
        (sum, location) => sum + (location.sub_qty || 0),
        0
      );

      if (totalQuantity > 0) {
        parallelOps.push(
          updateShipmentItem({
            variables: {
              id: shipmentItemId,
              quantity: totalQuantity,
              expectedQuantity,
              rackLocations: itemRackLocations,
            },
            refetchQueries: [],
          })
        );

        parallelOps.push(
          createTransactionMutation({
            variables: {
              assetId: newAsset.id,
              code: newAsset.assetId,
              quantity: totalQuantity,
              notes: "",
              senderName: "",
              carrierName: "",
              trackingNumber: "",
              orderNumber: "",
              poNumber: "",
              rackLocations: itemRackLocations,
              rackLocationsChanges: itemRackLocations,
              oldRackLocations: [{ sub_qty: 0 }],
              shipmentItemId,
              actionSource: "transaction_modal",
              skipRefreshCache: true,
              skipLocationUpdates: true,
            },
            refetchQueries: [],
          })
        );
      }
    } else if (expectedQuantity) {
      parallelOps.push(
        updateShipmentItem({
          variables: {
            id: shipmentItemId,
            quantity: shipmentItemQuantity || 0,
            expectedQuantity,
          },
          refetchQueries: [],
        })
      );
    }

    if (parallelOps.length > 0) {
      await Promise.all(parallelOps);
    }
  };

  const handleBulkDuplicateConfirm = async (quantity, options = {}) => {
    const { incrementBarcode = false, serialNumbers = [] } = options;
    const CONCURRENCY_LIMIT = 1;
    setBulkDuplicateLoading(true);

    try {
      const errors = await validateForm();
      const formIsValid = Object.keys(errors).length === 0;

      if (!formIsValid) {
        showValidationError();
        setBulkDuplicateLoading(false);
        return;
      }

      // Only save the current item if the form has unsaved changes
      if (dirty) {
        await submitForm();
      }

      const input = getInput(values);
      const baseBarcode = values?.barcode;
      const duplicatesToCreate =
        serialNumbers && serialNumbers.length > 0 ? serialNumbers.length : quantity - 1;

      setBulkDuplicateProgress({ current: 0, total: duplicatesToCreate });

      // Build all task inputs upfront
      const tasks = [];
      for (let i = 0; i < duplicatesToCreate; i++) {
        let processedInput = {
          ...input,
          ...(shipment.costCenter?.id && { costCenterId: parseInt(shipment.costCenter?.id, 10) }),
        };

        if (incrementBarcode && baseBarcode && /^\d+$/.test(baseBarcode)) {
          const barcodeLength = baseBarcode.length;
          const numericBarcode = parseInt(baseBarcode, 10);
          const newBarcodeNumber = String(numericBarcode + i + 1).padStart(barcodeLength, "0");
          processedInput = { ...processedInput, barcode: newBarcodeNumber };
        }

        if (serialNumbers && serialNumbers.length > 0 && i < serialNumbers.length) {
          processedInput = { ...processedInput, serialNumber: serialNumbers[i] };
        }

        tasks.push({
          processedInput,
          images: asset.images,
          rackLocations,
          expectedQuantity: values.expectedQuantity,
          shipmentItemQuantity: values.shipmentItemQuantity,
        });
      }

      // Process tasks with limited concurrency
      let completed = 0;
      const executing = new Set();

      for (const task of tasks) {
        const promise = createSingleDuplicate(task)
          .then(() => {
            completed++;
            setBulkDuplicateProgress({ current: completed, total: duplicatesToCreate });
          })
          .catch(err => {
            console.error("Error duplicating item:", err);
            completed++;
            setBulkDuplicateProgress({ current: completed, total: duplicatesToCreate });
          })
          .finally(() => executing.delete(promise));

        executing.add(promise);

        if (executing.size >= CONCURRENCY_LIMIT) {
          await Promise.race(executing);
        }
      }

      // Wait for remaining in-flight items to finish
      await Promise.all(executing);

      await client.query({
        query: GET_SHIPMENT_QUERY,
        variables: { id: shipmentId },
        fetchPolicy: "network-only",
      });
      notifySuccess(`Successfully duplicated`);

      const newParams = new URLSearchParams(window.location.search);
      newParams.delete("bulkDuplicateModal");
      newParams.delete("addShipmentItem");
      navigate({ search: newParams.toString() });
      closeItemDetails();
    } catch (error) {
      console.error("Error in bulk duplication:", error);
      notifyError("Failed to duplicate items. Please try again.");
    } finally {
      setBulkDuplicateLoading(false);
      setBulkDuplicateProgress(null);
    }
  };

  const handleBulkDuplicateClose = () => {
    const newParams = new URLSearchParams(window.location.search);
    newParams.delete("bulkDuplicateModal");
    navigate({ search: newParams.toString() });
  };

  return (
    <Box sx={styles.container}>
      <Grid container spacing={1}>
        <Grid size={6}>
          <Button
            fullWidth
            variant="outlined"
            disabled={isDeleteDisabled}
            startIcon={removing && <CircularProgress size={22} />}
            onClick={toggleRemoveItem}
          >
            Delete
          </Button>
        </Grid>
        <Grid size={6}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              fullWidth
              sx={styles.saveButton}
              type="button"
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaveDisabled}
              startIcon={isSubmitting && <CircularProgress size={22} />}
            >
              Save
            </Button>
            <Box variant="contained" aria-label="split button">
              <Button
                disabled={isSaveDisabled}
                variant="contained"
                color="primary"
                {...bindTrigger(popupState)}
                sx={styles.dropdownButton}
              >
                <KeyboardArrowDownIcon
                  sx={{ transform: popupState.isOpen ? undefined : "rotate(180deg)" }}
                />
              </Button>
              <Menu {...bindMenu(popupState)}>
                <MenuItem onClick={handleDuplicate}>
                  <ListItemIcon>
                    <DuplicateIcon />
                  </ListItemIcon>
                  <ListItemText>Duplicate Item</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleBulkDuplicate}>
                  <ListItemIcon>
                    <BulkDuplicateIcon />
                  </ListItemIcon>
                  <ListItemText>Bulk Duplicate Item</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <BulkDuplicateModal
        open={bulkDuplicateModalOpen}
        onClose={handleBulkDuplicateClose}
        onConfirm={handleBulkDuplicateConfirm}
        loading={bulkDuplicateLoading}
        progress={bulkDuplicateProgress}
        showBarcodeOption={!!values.barcode}
        serialNumberEnabled={!!values.serialNumber}
        shipmentSerialNumbers={shipmentSerialNumbers}
      />

      {validationErrorOpen && <ValidationErrorModal onClose={handleCloseValidationError} />}
    </Box>
  );
}
